// Keyboard cheat sheet overlay. Opens via:
//   * the `?` key anywhere (handled by useKeyboardShortcuts)
//   * "Keyboard shortcuts" in the user menu (passes openCheatSheet)
//
// Per shared-chrome-v2 §8 the layout is three columns: GLOBAL,
// GO TO, LISTS & DETAIL. Shortcuts render as small key-cap pills.
//
// Esc closes; click outside the sheet closes; both are handled by
// the keyboard hook (Esc) and this component's outside-click watch.

import { useEffect, useMemo, useRef } from "react";

import { useChrome } from "./ChromeContext";
import {
  detectMacLikePlatform,
  shortcutModifierLabel,
} from "../hooks/keyboard-shortcuts-helpers";

interface ShortcutRow {
  keys: string[];
  label: string;
}

// S2 PR 4 (audit 8.5) — the modifier key renders per-platform
// ("Ctrl" on Windows, "⌘" on mac) instead of mac-glyph-only.
function buildGlobalRows(modifier: string): ShortcutRow[] {
  return [
    { keys: [modifier, "K"], label: "Search & Ask Stew" },
    { keys: [modifier, "/"], label: "Open / close the left panel" },
    { keys: [modifier, ","], label: "Open preferences" },
    { keys: ["?"], label: "Open this cheat sheet" },
    { keys: ["Esc"], label: "Close overlay / go back" },
  ];
}

const GO_TO_ROWS: ShortcutRow[] = [
  { keys: ["G", "D"], label: "Dashboard" },
  { keys: ["G", "H"], label: "Households" },
  { keys: ["G", "C"], label: "Contacts" },
  { keys: ["G", "O"], label: "Opportunities" },
  { keys: ["G", "P"], label: "Processing" },
  { keys: ["G", "G"], label: "Ongoing Care" },
  { keys: ["G", "A"], label: "Automations (admin)" },
  { keys: ["G", "N"], label: "Reports" },
];

const LIST_DETAIL_ROWS: ShortcutRow[] = [
  { keys: ["J"], label: "Next row" },
  { keys: ["K"], label: "Previous row" },
  { keys: ["Enter"], label: "Open highlighted row" },
  { keys: ["E"], label: "Edit / export (context-aware)" },
  { keys: ["F"], label: "Filter panel" },
  { keys: ["N"], label: "New record" },
  { keys: ["C"], label: "Add note" },
  { keys: ["T"], label: "Add task" },
];

export function KeyboardCheatSheet() {
  const chrome = useChrome();
  const isOpen = chrome.isCheatSheetOpen;
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const globalRows = useMemo(
    () => buildGlobalRows(shortcutModifierLabel(detectMacLikePlatform())),
    [],
  );

  // Outside-click closes. Esc is handled by the global keyboard
  // shortcut hook so it works even when the cheat sheet doesn't have
  // a focused descendant.
  useEffect(() => {
    if (!isOpen) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (sheetRef.current && target && !sheetRef.current.contains(target)) {
        chrome.closeCheatSheet();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, chrome]);

  if (!isOpen) return null;

  return (
    <div
      className="overlay-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        ref={sheetRef}
        className="cheat-sheet"
        data-testid="keyboard-cheat-sheet"
      >
        <div className="cheat-sheet-header">
          <h2>Keyboard shortcuts</h2>
          <button
            type="button"
            className="cheat-sheet-close"
            aria-label="Close keyboard shortcuts"
            onClick={chrome.closeCheatSheet}
          >
            ×
          </button>
        </div>
        <div className="cheat-sheet-grid">
          <ShortcutColumn title="Global" rows={globalRows} />
          <ShortcutColumn title="Go to" rows={GO_TO_ROWS} />
          <ShortcutColumn title="Lists & detail" rows={LIST_DETAIL_ROWS} />
        </div>
      </div>
    </div>
  );
}

function ShortcutColumn({
  title,
  rows,
}: {
  title: string;
  rows: ShortcutRow[];
}) {
  return (
    <div className="cheat-sheet-column">
      <h3 className="cheat-sheet-column-title">{title}</h3>
      <ul className="cheat-sheet-list">
        {rows.map((row, idx) => (
          <li key={`${row.label}-${idx}`}>
            <span className="cheat-sheet-keys">
              {row.keys.map((k, i) => (
                <kbd key={`${k}-${i}`} className="kbd">
                  {k}
                </kbd>
              ))}
            </span>
            <span className="cheat-sheet-label">{row.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
